import { useEffect } from 'react'

import { Activity, isUnreadComment, isUnreadReply } from '../utils'
import {
  ArtboardUnreadFragmentDoc,
  CommentInfoFragment,
  useUpdateCommentsMutation,
  GetDetailViewArtboardDocument,
  GetDetailViewArtboardQuery,
  GetDetailViewArtboardQueryVariables,
} from 'gqlTypes'
import { dataIdFromObject } from 'graphql/cache'
import { DataProxy } from 'apollo-cache'
import produce from 'immer'
import { ErrorHandler } from 'modules/tracing'
import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'
import { updateCommentFromCache, updateReplyFromCache } from './utils'

const MARK_COMMENTS_AS_READ_TIMEOUT = 2000

interface UseDelayedMarkCommentsOrRepliesAsReadProps {
  parentComment?: CommentInfoFragment
  activities: Activity[]
  permanentArtboardShortId: string
  shareIdentifier: string
}

interface UpdateArtboardFromCache {
  cache: DataProxy
  permanentArtboardShortId: string
  numberOfUnreadComments: number
  shareIdentifier: string
}

const decreaseArtboardUnreadCountFromCache = ({
  cache,
  permanentArtboardShortId,
  numberOfUnreadComments,
  shareIdentifier,
}: UpdateArtboardFromCache) => {
  const artboardFromCache = cache.readQuery<
    GetDetailViewArtboardQuery,
    GetDetailViewArtboardQueryVariables
  >({
    query: GetDetailViewArtboardDocument,
    variables: {
      permanentArtboardShortId,
      shareIdentifier,
    },
  })

  if (artboardFromCache) {
    const updatedArtboard = produce(artboardFromCache?.artboard, artboard => {
      if (artboard?.artboardBase) {
        artboard.artboardBase.unreadCount! -= numberOfUnreadComments
      }
    })

    cache.writeQuery<
      GetDetailViewArtboardQuery,
      GetDetailViewArtboardQueryVariables
    >({
      query: GetDetailViewArtboardDocument,
      variables: {
        permanentArtboardShortId,
        shareIdentifier,
      },
      data: {
        __typename: 'RootQueryType',
        artboard: updatedArtboard,
      },
    })
  }
}

// Marks comments or replies as read after 2 seconds
export const useDelayedMarkCommentsOrRepliesAsRead = ({
  activities,
  parentComment,
  permanentArtboardShortId,
  shareIdentifier,
}: UseDelayedMarkCommentsOrRepliesAsReadProps) => {
  const [updateComments] = useUpdateCommentsMutation({
    redirectErrors: true,
    onError: error => {
      ErrorHandler.shouldNeverHappen(
        `There was an error trying to mark comments as read, Reason: ${error.message}`
      )
    },
  })
  const { isThreadRoute } = useThreadRouteParams()

  useEffect(() => {
    const markAsRead = () => {
      // We need to differentiate between comments and replies, because
      // in the activity list (seeing comments), we shouldn't mark the
      // replies as read, also the other way around, in a thread view
      // we shouldn't mark all comments as read
      if (isThreadRoute) {
        if (!parentComment) {
          ErrorHandler.shouldNeverHappen('A parent comment should be provided.')
          return null
        }

        // Mark replies as read in thread view
        const unreadReplies = activities.filter(isUnreadReply)
        const identifiers = unreadReplies.map(reply => reply.identifier)

        if (!parentComment.isRead) {
          identifiers.push(parentComment.identifier)
        }

        if (identifiers.length) {
          updateComments({
            variables: {
              identifiers,
              input: { isRead: true },
            },
            update: cache => {
              if (!parentComment.isRead) {
                // Update parent comment as read in the cache
                updateCommentFromCache({
                  cache,
                  comment: {
                    ...parentComment,
                    isRead: true,
                  },
                })
              }

              // Update every reply as read in the cache
              unreadReplies.forEach(reply => {
                updateReplyFromCache({
                  cache,
                  comment: {
                    ...reply,
                    isRead: true,
                  },
                })
              })

              // Decrease unreadCount for the artboard
              decreaseArtboardUnreadCountFromCache({
                cache,
                permanentArtboardShortId,
                numberOfUnreadComments: identifiers.length,
                shareIdentifier,
              })
            },
          })
        }
      } else {
        // Mark comments as read in activities
        const unreadComments = activities.filter(isUnreadComment)
        const identifiers = unreadComments.map(comment => comment.identifier)

        if (unreadComments.length) {
          updateComments({
            variables: {
              identifiers,
              input: { isRead: true },
            },
            update: cache => {
              // Resets unreadCount from ArtboardBase
              cache.writeFragment({
                id: dataIdFromObject({
                  __typename: 'ArtboardBase',
                  shortId: permanentArtboardShortId,
                }) as string,
                fragment: ArtboardUnreadFragmentDoc,
                data: {
                  __typename: 'ArtboardBase',
                  shortId: permanentArtboardShortId,
                  unreadCount: 0,
                },
              })

              // Update every comment as read in the cache
              unreadComments.forEach(comment => {
                updateCommentFromCache({
                  cache,
                  comment: {
                    ...comment,
                    isRead: true,
                  },
                })
              })
            },
          })
        }
      }
    }
    const timer = setTimeout(() => {
      try {
        markAsRead()
      } catch (error) {
        ErrorHandler.shouldNeverHappen(
          `There was an error trying to update cache data after marking comments as read, Reason: ${error?.message}`
        )
      }
    }, MARK_COMMENTS_AS_READ_TIMEOUT)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
